import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getCategoryBySlug } from '@/mocks/categories';
import { getDefaultArticleContent } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getDefaultArticleContent(slug || '');
  const category = getCategoryBySlug(article.category);

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '黄油游戏下载',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.huangyougame.com/article/${article.slug}`,
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.huangyougame.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '游戏分类',
        item: `https://www.huangyougame.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://www.huangyougame.com/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(articleLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbLd)}
      </script>

      <Navbar />

      <main className="pt-16">
        <article>
          <header className="bg-background-100 py-8 md:py-10">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category?.name || '游戏分类', href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <div className="mt-6">
                {category && (
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4 cursor-pointer hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    <i className={`${category.icon} text-xs`}></i>
                    {category.name}
                  </Link>
                )}
                <h1 className="text-xl md:text-2xl lg:text-3xl font-heading text-foreground-900 mb-3 leading-tight">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 mb-4 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <section className="py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="prose prose-sm md:prose-base max-w-none">
                {article.sections.map((section, index) => (
                  <div key={index} className="mb-8">
                    <h2 className="text-lg md:text-xl font-heading text-foreground-900 mb-3">
                      {index === 0 ? '' : `${index}. `}{section.heading}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500 mb-1">觉得有用？</p>
                    <p className="text-sm font-medium text-foreground-700">
                      分享给更多需要的玩家吧
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-share-forward-line"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-thumb-up-line"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-background-100 rounded-lg p-5 md:p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-3">
                  相关教程推荐
                </h3>
                <div className="space-y-3">
                  <Link
                    to={`/category/${article.category}`}
                    className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-folder-line"></i>
                    查看{category?.name || '更多'}分类下的其他教程
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </>
  );
}